import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productsByCategory, allProducts, productCategories, type Product } from '@/mocks/products';

interface ProductCardProps {
  product: Product;
}

function ProductCard({ product }: ProductCardProps) {
  return (
    <a
      href={product.url}
      target="_blank"
      rel="noopener nofollow noreferrer"
      className="group block rounded-xl bg-white border border-background-200 hover:border-primary-300 overflow-hidden transition-all duration-200 hover:translate-y-[-2px] hover:shadow-md cursor-pointer"
    >
      <div className="aspect-square overflow-hidden bg-background-100">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20geometric%20pattern%20soft%20pastel%20colors%20clean%20modern%20background%20texture&width=300&height=300&seq=product-fallback&orientation=squarish';
          }}
        />
      </div>
      <div className="p-3">
        <h3 className="text-xs font-semibold text-foreground-800 leading-tight line-clamp-2 mb-1.5 group-hover:text-primary-600 transition-colors">
          {product.name}
        </h3>
        <p className="text-xs font-bold text-primary-600">
          {product.price}
        </p>
      </div>
    </a>
  );
}

export default function ProductsPage() {
  const { t } = useTranslation('common');
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const filteredProducts = activeCategory === 'All'
    ? allProducts
    : productsByCategory[activeCategory] || [];

  const categoryCount = (cat: string) => {
    return (productsByCategory[cat] || []).length;
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative py-16 md:py-20 bg-white border-b border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
            {t('products.pageTitle', 'All Products')}
          </h1>
          <p className="text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            {t('products.pageDescription', 'Browse all curated products from our spreadsheet. Click any item to view details and purchase on our partner site.')}
          </p>
          <div className="mt-6 text-sm text-foreground-400">
            {allProducts.length} {t('products.items', 'products')} · {productCategories.length} {t('products.categories', 'categories')}
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-6 bg-white border-b border-background-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <button
              onClick={() => setActiveCategory('All')}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === 'All'
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {t('products.all', 'All')} ({allProducts.length})
            </button>
            {productCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat} ({categoryCount(cat)})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-padding bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {activeCategory === 'All' ? (
            // Show all categories grouped
            <div>
              {productCategories.map((cat) => {
                const products = productsByCategory[cat] || [];
                if (products.length === 0) return null;
                return (
                  <div key={cat} className="mb-12">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-800 mb-5">
                      {cat}
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                      {products.map((product) => (
                        <ProductCard key={product.id} product={product} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Show filtered products
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-800 mb-5">
                {activeCategory}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}